import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import type { useTermsAcceptance } from '@/hooks/useTermsAcceptance'
import styles from './TermsAcceptanceModal.module.css'

type TermsAcceptanceModalProps = ReturnType<typeof useTermsAcceptance>

export function TermsAcceptanceModal({
  terms,
  termsError,
  isTermsLoading,
  scrollRef,
  hasScrolledToEnd,
  hasAgreed,
  confirmationPhrase,
  setConfirmationPhrase,
  submitError,
  isSubmitting,
  canSubmit,
  confirmationPhraseHint,
  handleScroll,
  handleAgreeChange,
  handleSubmit,
  retryLoadTerms,
}: TermsAcceptanceModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const phraseInputRef = useRef<HTMLInputElement>(null)
  const [phraseCopied, setPhraseCopied] = useState(false)

  const handleCopyPhrase = useCallback(async () => {
    const phrase = confirmationPhraseHint.trim()
    if (!phrase) return

    setConfirmationPhrase(phrase)

    try {
      await navigator.clipboard.writeText(phrase)
      setPhraseCopied(true)
      window.setTimeout(() => setPhraseCopied(false), 2500)
    } catch {
      // Sem permissão de clipboard: a frase já foi preenchida no campo.
    }

    phraseInputRef.current?.focus()
  }, [confirmationPhraseHint, setConfirmationPhrase])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  return (
    <div
      className={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          event.preventDefault()
        }
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-acceptance-title"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
          }
        }}
      >
        <header className={styles.header}>
          <p className={styles.eyebrow}>Obrigatório</p>
          <h2 id="terms-acceptance-title" className={styles.title}>
            Termos da Plataforma
          </h2>
          <p className={styles.subtitle}>
            Leia o documento abaixo, confirme sua concordância e aceite para continuar usando o
            painel.
          </p>
          {terms?.bundle_version && (
            <span className={styles.versionBadge}>Versão {terms.bundle_version}</span>
          )}
        </header>

        {isTermsLoading && <p className={styles.loading}>Carregando termos da plataforma...</p>}

        {termsError && !isTermsLoading && (
          <div className={styles.footer}>
            <p className={styles.error} role="alert">
              {termsError}
            </p>
            <button type="button" className={styles.submitButton} onClick={() => void retryLoadTerms()}>
              Tentar novamente
            </button>
          </div>
        )}

        {terms && !isTermsLoading && !termsError && (
          <>
            <div ref={scrollRef} className={styles.body} onScroll={handleScroll}>
              {terms.documents.map((document) => (
                <section key={document.document_type} className={styles.docSection}>
                  <h3 className={styles.docTitle}>
                    {document.title}
                    <span className={styles.docVersion}>v{document.version}</span>
                  </h3>
                  <pre className={styles.content}>{document.content}</pre>
                </section>
              ))}
            </div>

            {!hasScrolledToEnd && (
              <p className={styles.scrollHint}>
                Role até o final do texto para habilitar a confirmação.
              </p>
            )}

            <footer className={styles.footer}>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={hasAgreed}
                  disabled={!hasScrolledToEnd}
                  onChange={(event) => handleAgreeChange(event.target.checked)}
                />
                <span
                  className={
                    hasScrolledToEnd ? styles.checkboxLabel : styles.checkboxLabelDisabled
                  }
                >
                  Li e concordo com os termos
                </span>
              </label>

              {hasAgreed && (
                <div className={styles.confirmationBlock}>
                  <p className={styles.confirmationLabel}>
                    Para confirmar, digite exatamente a frase abaixo (ou use Copiar frase):
                  </p>
                  <div className={styles.phraseRow}>
                    <p className={styles.confirmationHint}>{confirmationPhraseHint}</p>
                    <button
                      type="button"
                      className={styles.copyPhraseButton}
                      onClick={() => void handleCopyPhrase()}
                    >
                      <Icon icon={phraseCopied ? 'mdi:check' : 'mdi:content-copy'} />
                      {phraseCopied ? 'Copiado' : 'Copiar frase'}
                    </button>
                  </div>
                  <input
                    ref={phraseInputRef}
                    type="text"
                    className={styles.input}
                    value={confirmationPhrase}
                    onChange={(event) => setConfirmationPhrase(event.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    aria-label="Frase de confirmação dos termos"
                  />
                </div>
              )}

              {submitError && (
                <p className={styles.error} role="alert">
                  {submitError}
                </p>
              )}

              <button
                type="button"
                className={styles.submitButton}
                disabled={!canSubmit}
                onClick={() => void handleSubmit()}
              >
                {isSubmitting ? 'Registrando aceite...' : 'Aceitar termos'}
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  )
}
